import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { getImageUrl } from '@/utils/urls';
import { getItemCardModel } from '@/redux/modules/itemCardModel';
import { getItemContextMessageIds } from '@/utils/modalContextMessage';
import { getModalContext } from '@/redux/modules/modal';
import { useAppSelector } from '@/redux/hooks';
import ImageWithFallback from '@liveauctioneers/caterwaul-components/lib/ImageWithFallback/ImageWithFallback2';
import isEqual from 'react-fast-compare';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import Modal from '@liveauctioneers/caterwaul-components/lib/Modal/Modal';
import ModalFooter from '@liveauctioneers/caterwaul-components/lib/Modal/ModalFooter';
import ModalHeader from '@liveauctioneers/caterwaul-components/lib/Modal/ModalHeader';
import SignUpEmail from './SignUpEmail';
import styled from 'styled-components';
import Text from '@liveauctioneers/caterwaul-components/lib/Text/Text';

type Props = {
    bidderExists: boolean;
    cancelSignUp: (event: any) => void;
    error: string;
    openLoginModal: Function;
    setEmail: Function;
    show: boolean;
    submitCreateAccount: Function;
    submitted: boolean;
    whiteLabel: boolean;
};

const ItemContextSignUpModal = (props: Props) => {
    const {
        bidderExists,
        cancelSignUp,
        error = '',
        openLoginModal,
        setEmail,
        show,
        submitCreateAccount,
        submitted = false,
    } = props;

    const { id: itemId, type: contextType } = useAppSelector(getModalContext, isEqual);
    const { catalogId, imageVersion, photos, sellerId, title } = useAppSelector(
        (state) => getItemCardModel(state, itemId),
        isEqual
    );
    const imageUrl = getImageUrl({
        catalogId,
        imageNumber: photos[0],
        imageVersion,
        itemId,
        sellerId,
        width: 310,
    });

    const [headerMessageId, blurbMessageId] = getItemContextMessageIds(contextType);

    const headerContent = (
        <StyledHeader>
            <StyledImageWithFallback alt={title || 'Item'} src={imageUrl} />
            <Text h2>
                <FormattedMessage id={headerMessageId} />
            </Text>
        </StyledHeader>
    );

    const modalHeader = <ModalHeader onClose={cancelSignUp} title={headerContent} />;

    const modalBody = (
        <div>
            <StyledBlurbText secondary>
                <FormattedMessage id={blurbMessageId} />
            </StyledBlurbText>
            <SignUpEmail
                bidderExists={bidderExists}
                error={error}
                noBodyMargins
                setEmail={setEmail}
                submitCreateAccount={submitCreateAccount}
                submitted={submitted}
            />
        </div>
    );

    const modalFooter = (
        <ModalFooter>
            <StyledFooterText>
                <FormattedMessage
                    id="signUpModal.footer"
                    values={{
                        link: (
                            <Link key="link" onClick={openLoginModal}>
                                <StyledLinkText>
                                    <FormattedMessage id="signUpModal.footerLink" />
                                </StyledLinkText>
                            </Link>
                        ),
                    }}
                />
            </StyledFooterText>
        </ModalFooter>
    );

    return (
        <StyledModal
            body={modalBody}
            contentLabel="SignUpModal"
            footer={modalFooter}
            header={modalHeader}
            show={show}
        />
    );
};

export default ItemContextSignUpModal;

const StyledModal = styled(Modal).attrs(() => ({
    bodyClassName: 'bodyClassName',
}))`
    .bodyClassName {
        padding: 16px 0 20px 0;
        margin: 0 auto;
        width: 100%;
        max-width: 400px;

        @media (max-width: 767px) {
            padding: 15px 20px 40px;
        }
    }
`;

const StyledHeader = styled.div`
    margin-top: -41px;
`;

const StyledImageWithFallback = styled(ImageWithFallback)`
    max-height: 74px;
    max-width: 80%;
    margin-bottom: 25px;
`;

const StyledBlurbText = styled(Text)`
    text-align: center;
    margin-bottom: 36px;
    display: block;
    & + div {
        padding: 0;
    }
`;

const StyledFooterText = styled(Text)`
    font-family: ${({ theme }) => theme.typography.fontPrimary};
    font-size: 14px;
    line-height: 20px;
    text-align: center;
    letter-spacing: 0.5px;
`;

const StyledLinkText = styled(StyledFooterText)`
    font-family: ${({ theme }) => theme.typography.fontPrimaryStrong};
    color: ${({ theme }) => theme.colors.blue100};
`;
